// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  CustomerTriage,
  CustomerTriageFilter,
} from '@/customer/api/triage-types';
import { CustomerTriageClassifyDialog } from '@/customer/components/customer-triage-classify-dialog';
import { CustomerTriageDismissDialog } from '@/customer/components/customer-triage-dismiss-dialog';
import { CustomerTriageTableRow } from '@/customer/components/customer-triage-table-row';
import { useTriageCustomers } from '@/customer/hooks/use-triage-customers';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useState } from 'react';

const PAGE_SIZE = 50;

interface CustomerTriageTableProps {
  filter?: CustomerTriageFilter;
  onPageChange: (page: number) => void;
}

export function CustomerTriageTable(props: CustomerTriageTableProps) {
  const { customers, resultCount } = useTriageCustomers({
    filter: props.filter,
  });
  const classifyDialog = useDisclosure();
  const dismissDialog = useDisclosure();
  const [selectedTriage, setSelectedTriage] = useState<CustomerTriage | null>(
    null
  );
  const [triageToDismiss, setTriageToDismiss] = useState<CustomerTriage | null>(
    null
  );

  const pageNumber = props.filter?.page ?? 1;
  const onPageChange = props.onPageChange;

  const handleClassifyClick = (triage: CustomerTriage) => {
    setSelectedTriage(triage);
    classifyDialog.open();
  };

  const handleDismissClick = (triage: CustomerTriage) => {
    setTriageToDismiss(triage);
    dismissDialog.open();
  };

  const handleClassifyClose = () => {
    classifyDialog.close();
    setSelectedTriage(null);
  };

  const handleDismissClose = () => {
    dismissDialog.close();
    setTriageToDismiss(null);
  };

  useEffect(() => {
    if (onPageChange && customers.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [customers.length, pageNumber, onPageChange]);

  const emptyStateContent = (() => {
    switch (props.filter?.status) {
      case 'MATCHED':
        return {
          statusLabel: 'matched',
          message:
            'Customers that have been classified in Hardfin will appear here',
        };
      case 'DISMISSED':
        return {
          statusLabel: 'dismissed',
          message:
            'Customers that have been dismissed from the review queue will appear here',
        };
      default:
        return {
          statusLabel: 'need review',
          message:
            'Customers from QuickBooks that need classification in Hardfin will appear here',
        };
    }
  })();

  return (
    <>
      <CustomerTriageClassifyDialog
        isOpen={classifyDialog.isOpen}
        triage={selectedTriage}
        onClose={handleClassifyClose}
      />
      <CustomerTriageDismissDialog
        isOpen={dismissDialog.isOpen}
        triage={triageToDismiss}
        onClose={handleDismissClose}
      />
      {customers.length === 0 ? (
        <EmptyState
          title={`No customers ${emptyStateContent.statusLabel}`}
          message={emptyStateContent.message}
        />
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Display name</TableCell>
                  <TableCell>Company name</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>First synced</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={200} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((triage) => (
                  <CustomerTriageTableRow
                    key={triage.id}
                    triage={triage}
                    onClassifyClick={handleClassifyClick}
                    onDismissClick={handleDismissClick}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePaginationFooter
            pagination={{
              page: pageNumber,
              pageSize: PAGE_SIZE,
              onPageChange,
            }}
            totalCount={resultCount}
            entityOptions={{
              singular: 'customer',
              plural: 'customers',
              includeCount: false,
            }}
          />
        </>
      )}
    </>
  );
}
